/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, FormControl, Grid, IconButton, InputAdornment } from '@material-ui/core';
import { cloneDeep } from 'lodash';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { DateRange } from '@material-ui/pickers';
import { useStyles } from './styles';
import { AllocatedIcon, FieldLabel, Header, InputControl, NotAllocatedIcon, OverflowTip, PageTitle, ReportTitle, SearchIcon } from '../../../components';
import { useApiError, useAppData, useLoading } from '../../../providers';
import { getCurrencies, getPartnerPartnerships } from '../../IBDashboard';
import { PartnerPartnership } from '../../../models';
import { ClientAssignmentCheckModel, ClientListEmailReportRequest, ClientListItem, reportEnColumns } from './ClientListModel';
import { ReportTable, TopSelectPanel } from './components';
import { checkClient, getClientsReport, sendEmailClientsOverviewReport } from './ClientListService';
import { getSelectedColumnsInEn, getTranslatedColumnsInCorrectOrder, getTranslation } from '../../../utils';
import { trackAmplitudeData } from '../../../services/amplitudeService';
import { useCommonStyles } from '../../../styles';
import { ColumnConfig, formatNumber, formatDate, formatAbsolute } from '../DataTable';

export const ClientsList: FC = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { setIsLoading } = useLoading();
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>(['All']);
  const [partnerships, setPartnerships] = useState<PartnerPartnership[]>([]);
  const [selectedPartnerships, setSelectedPartnerships] = useState<string[]>(['All']);
  const { addError } = useApiError();
  const { translations, userData } = useAppData();
  const [translation, setTranslation] = useState<any>([]);
  const [allReportData, setAllReportData] = useState<ClientListItem[]>([]);
  const [reportData, setReportData] = useState<ClientListItem[]>([]);
  const [groupBy, setGroupBy] = useState<string>('noGrouping');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [showInactive, setShowInactive] = useState<boolean>(false);
  const [showOnlyDepositsWithdrawals, setShowOnlyDepositsWithdrawals] = useState<boolean>(false);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange<Date>>([moment().subtract(1, 'months').toDate(), moment().subtract(1, 'days').toDate()]);
  const { enqueueSnackbar } = useSnackbar();
  const [userEmail, setUserEmail] = useState<string>();
  const [email, setEmail] = useState<string | number>();
  const [allocationData, setAllocationData] = useState<ClientAssignmentCheckModel | undefined>();

  const sendAllocationEmail = () => {
    if (allocationData && !allocationData.isAssigned) {
      window.open(`mailto:${allocationData.supportEmail}`);
    }
  };

  const getReportFiltersData = useCallback(async () => {
    try {
      const currency = await getCurrencies();
      setCurrencies(currency.data);
      const response = await getPartnerPartnerships(true);
      setPartnerships(response.data);
    } catch (error) {
      addError(JSON.stringify(error.response));
    }
  }, [addError]);

  const getReportData = useCallback(
    async (partnershipList: number[], currencyList: string[], onlyDepositsWithdrawals: boolean, dateRange?: DateRange<Date>) => {
      try {
        const fromDate = dateRange?.[0] ? moment(dateRange[0]).format('YYYY-MM-DD') : undefined;
        const toDate = dateRange?.[1] ? moment(dateRange[1]).format('YYYY-MM-DD') : undefined;

        const response = await getClientsReport(partnershipList, currencyList, onlyDepositsWithdrawals, fromDate, toDate);

        setAllReportData(response.data);
        setReportData(response.data);
      } catch (error) {
        addError(JSON.stringify(error.response));
      }
    },
    [addError],
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await getReportFiltersData();
      await getReportData([], [], false, undefined);
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    setTranslation(JSON.parse(translations));
  }, [translations]);

  useEffect(() => {
    const user = JSON.parse(userData);
    setUserEmail(user.userEmail);
  }, [userData]);

  const columns = useMemo((): ColumnConfig<ClientListItem>[] => {
    const first4: ColumnConfig<ClientListItem>[] = [
      { key: 'partnershipCode', label: translation.text_6596 || '', type: 'string' },
      { key: 'clientName', label: translation.text_6597 || '', type: 'string' },
      { key: 'clientCountry', label: translation.text_6599 || '', type: 'string', format: (v) => (v ? getTranslation(translation, v) : '-') },
      { key: 'currency', label: translation.text_6600 || '', type: 'string' },
    ];

    const rest: ColumnConfig<ClientListItem>[] = [
      {
        key: 'accountType',
        label: translation.text_6601 || '',
        type: 'string',
        format: (v) =>
          v ? (
            <OverflowTip interactive title={getTranslation(translation, v)}>
              {getTranslation(translation, v)}
            </OverflowTip>
          ) : (
            '-'
          ),
        exportFormat: (v) => (v ? getTranslation(translation, v) : '-'),
      },
      { key: 'partnershipStartDate', label: translation.text_6602 || '', type: 'date', format: (v) => formatDate(v) },
      { key: 'clientLogin', label: translation.text_6603 || '', type: 'number' },
      { key: 'partnershipEndDate', label: translation.text_6604 || '', type: 'date', format: (v) => formatDate(v) },
      { key: 'lastTradeDate', label: translation.text_6605 || '', type: 'date', format: (v) => formatDate(v) },
      { key: 'leverage', label: translation.text_6606 || '', type: 'number' },
      { key: 'volume', label: translation.text_6607 || '', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'volumeLots', label: translation.text_6608 || '', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'closedPositions', label: translation.text_6609 || '', type: 'number', summable: true },
      { key: 'deposits', label: translation.text_6610 || '', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'withDrawals', label: translation.text_6611 || '', type: 'number', format: (v) => formatAbsolute(v), summable: true },
      { key: 'netDeposits', label: translation.text_6612 || '', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'realEquity', label: translation.text_6613 || '', type: 'number', format: (v) => formatNumber(v), summable: true },
    ];

    if (groupBy === 'clientCountry') return [first4[2], first4[0], first4[1], first4[3], ...rest];
    if (groupBy === 'currency') return [first4[3], first4[0], first4[1], first4[2], ...rest];
    return [...first4, ...rest];
  }, [translation, groupBy]);

  // Derived string[] needed only for email utilities
  const columnNames = useMemo(() => columns.map((col) => col.label), [columns]);

  useEffect(() => {
    if (columnNames.length === 17) {
      setSelectedColumns([...columnNames.slice(0, 7), columnNames[16]]);
    }
  }, [columnNames]);

  const onPartnershipChange = useCallback(
    async (partnershipIds: number[], currenciesList: string[]) => {
      await getReportData(partnershipIds, currenciesList, showOnlyDepositsWithdrawals, selectedDateRange);
    },
    [getReportData, showOnlyDepositsWithdrawals, selectedDateRange],
  );

  const handleSelectChange = useCallback(
    async (options, type) => {
      const partnershipIds: number[] = [];
      let currenciesList: string[];

      if (type === 'Partnership') {
        setSelectedPartnerships(options);
        options.forEach((option: any) => {
          if (option !== 'All') {
            const partnership = partnerships.find((p) => p.partnershipCode === option);
            if (partnership !== undefined && partnership.id != null) {
              partnershipIds.push(partnership.id);
            }
          }
        });
        currenciesList = selectedCurrencies.includes('All') ? [] : selectedCurrencies;
      } else {
        setSelectedCurrencies(options);
        currenciesList = options.includes('All') ? [] : options;
        selectedPartnerships.forEach((option: any) => {
          if (option !== 'All') {
            const partnership = partnerships.find((p) => p.partnershipCode === option);
            if (partnership !== undefined && partnership.id != null) {
              partnershipIds.push(partnership.id);
            }
          }
        });
      }
      await onPartnershipChange(partnershipIds, currenciesList);
    },
    [partnerships, selectedPartnerships, selectedCurrencies, onPartnershipChange, setSelectedCurrencies, setSelectedPartnerships],
  );

  const handleShowInactiveChanges = useCallback(
    (show: boolean) => {
      setShowInactive(show);
      if (show) {
        const data = cloneDeep(allReportData).filter((report) => report.partnershipEndDate);
        setReportData(data);
      } else {
        setReportData(allReportData);
      }
    },
    [allReportData],
  );

  const getIdsByPartnershipCode = (objects: any[], partnershipCodes: string[]): number[] => {
    if (partnershipCodes.includes('All')) return [];
    const idMap = new Map<string, number>();
    objects.forEach((obj) => { idMap.set(obj.partnershipCode, obj.id); });
    return partnershipCodes.map((code) => idMap.get(code)).filter((id) => id !== undefined) as number[];
  };

  const handleFiltersApplied = useCallback(
    async (selected: string[], groupedBy: string, inactive: boolean, onlyDepositsWithdrawals: boolean, dateRange?: DateRange<Date>) => {
      setGroupBy(groupedBy);
      handleShowInactiveChanges(inactive);

      const nextDateRange = dateRange ?? selectedDateRange;
      const isCurrenciesChanged = selectedCurrencies !== selected;
      const isOnlyDepositsWithdrawalsChanged = onlyDepositsWithdrawals !== showOnlyDepositsWithdrawals;

      setSelectedCurrencies(selected);
      setShowOnlyDepositsWithdrawals(onlyDepositsWithdrawals);

      if (dateRange) setSelectedDateRange(dateRange);

      if (isCurrenciesChanged || isOnlyDepositsWithdrawalsChanged || dateRange) {
        setIsLoading(true);
        const partnershipIds = selectedPartnerships.length > 0 ? getIdsByPartnershipCode(partnerships, selectedPartnerships) : [];
        await getReportData(partnershipIds, selected.includes('All') ? [] : selected, onlyDepositsWithdrawals, nextDateRange);
        setIsLoading(false);
      }
    },
    [selectedDateRange, selectedCurrencies, showOnlyDepositsWithdrawals, partnerships, handleShowInactiveChanges, setIsLoading, getReportData, selectedPartnerships, getIdsByPartnershipCode],
  );

  const buildClientListEmailReportRequest = (): ClientListEmailReportRequest => ({
    partnersClientsFilters: {
      currencies: selectedCurrencies.includes('All') ? [] : selectedCurrencies,
      partnershipIds: getIdsByPartnershipCode(partnerships, selectedPartnerships),
    },
    reportEmailRequest: {
      email: null,
      includeTotal: false,
      tableColumns: getSelectedColumnsInEn(reportEnColumns, selectedColumns, columnNames),
      translatedTableColumns: getTranslatedColumnsInCorrectOrder(selectedColumns, columnNames),
      startDate: null,
      endDate: null,
    },
    showInactive,
  });

  const sendEmail = async () => {
    const clientListEmailReportRequest = buildClientListEmailReportRequest();
    try {
      trackAmplitudeData('partnerPortal_action_report', { type: 'SendByEmail', report_name: 'Clients Overview' });
      await sendEmailClientsOverviewReport(clientListEmailReportRequest);
      enqueueSnackbar(`${translation.text_1640} ${userEmail}.`, { variant: 'success' });
    } catch (error) {
      addError(JSON.stringify(error.response));
    } finally {
      setIsLoading(false);
    }
  };

  const emailChange = (event: any) => {
    setEmail(event.target.value);
  };

  const filterClients = useCallback(
    (allocationData: ClientAssignmentCheckModel) => {
      if (allocationData.isAssigned) {
        const data: any = cloneDeep(allReportData).filter((report) => allocationData.assignmentData.isDirectClient && report.clientName === allocationData.assignmentData.clientName);
        setReportData(data);
      } else {
        setReportData([]);
      }
    },
    [allReportData],
  );

  const checkAllocation = useCallback(
    async (emailString: string | number) => {
      try {
        setIsLoading(true);
        const response = await checkClient(emailString);
        setAllocationData(response.data);
        filterClients(response.data);
      } catch (error) {
        if (error.response.data.code === 'InvalidInput') {
          enqueueSnackbar(translation.allocation_check_wrong_input, { variant: 'error' });
        } else {
          addError(JSON.stringify(error.response));
        }
      } finally {
        setIsLoading(false);
      }
    },
    [email, addError, setIsLoading, enqueueSnackbar, translation, allReportData],
  );

  return (
    <>
      <Header title={translation.text_6663} />
      <Grid container direction="column" className={classes.root}>
        <PageTitle title={translation.text_6663} />
        <Grid container direction="column" className={commonClasses.mainContainer}>
          <ReportTitle
            sendEmail={sendEmail}
            title={translation.text_6429}
            subTitle={translation.clientOverviewTooltip}
            exportFileName={translation.text_6430}
            columns={columns}
            allData={reportData}
            selectedColumns={selectedColumns}
            printColumnNumber={selectedColumns.length}
            content={<ReportTable key={Math.random()} rows={reportData} groupByOption={groupBy} translation={translation} columns={columns} selectedColumns={selectedColumns} onSelectColumns={setSelectedColumns} />}
            enableButtons
            translation={translation}
            reportNameInEn="Clients Overview"
          />
          <Grid container direction="column">
            <TopSelectPanel
              translation={translation}
              partnerships={partnerships}
              currencies={currencies}
              dateRange={selectedDateRange}
              showInactive={showInactive}
              showOnlyDepositsWithdrawals={showOnlyDepositsWithdrawals}
              selectedPartnerships={selectedPartnerships}
              selectedCurrencies={selectedCurrencies}
              handleDateRangeChange={(value) => setSelectedDateRange(value)}
              onShowInactiveChange={handleShowInactiveChanges}
              onShowOnlyDepositsWithdrawalsChange={(show: boolean) => setShowOnlyDepositsWithdrawals(show)}
              onGroupByChange={(option: string) => setGroupBy(option)}
              onPartnershipCurrenciesChange={handleSelectChange}
              handleButtonClick={async () => {
                setIsLoading(true);
                const partnershipIds = selectedPartnerships.length > 0 ? getIdsByPartnershipCode(partnerships, selectedPartnerships) : [];
                await getReportData(partnershipIds, selectedCurrencies.includes('All') ? [] : selectedCurrencies, showOnlyDepositsWithdrawals, selectedDateRange);
                setIsLoading(false);
              }}
              handleFilterApplied={handleFiltersApplied}
              checkAllocation={async (email) => {
                setEmail(email);
                if (email === '') {
                  setReportData(allReportData);
                } else {
                  await checkAllocation(email);
                }
              }}
            />
            <Grid item xs={12} className={classes.mobileContainer}>
              <FieldLabel label={translation.email_trading_account} />
              <FormControl variant="outlined" margin="dense" className={classes.formControl}>
                <InputControl
                  value={email}
                  placeholder={translation.specify}
                  onChange={emailChange}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={async () => { if (email) await checkAllocation(email); }}>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Grid>
            {allocationData && allocationData.isAssigned && !allocationData.assignmentData.isDirectClient ? (
              <Grid container direction="column" alignContent="center" className={classes.clientAllocation}>
                <Grid item><AllocatedIcon /></Grid>
                <Grid item className={classes.allocationTitle}>{translation.client_is_allocated}</Grid>
                <Grid item className={classes.allocationText}>
                  {translation.partner_name} {allocationData.assignmentData.partnerName} ({allocationData.assignmentData.partnershipCode})
                </Grid>
              </Grid>
            ) : allocationData && !allocationData.isAssigned ? (
              <Grid container direction="column" alignContent="center" className={classes.clientAllocation}>
                <Grid item><NotAllocatedIcon /></Grid>
                <Grid item className={classes.allocationTitle}>{translation.client_is_not_allocated_to_you}</Grid>
                <Grid item className={classes.allocationText}>
                  {allocationData.partnershipCode ? `${translation.no_partnership_allocated} ${allocationData.partnershipCode}` : translation.has_multiple_partnerships}
                </Grid>
                <Grid item>{translation.please_contact_support}</Grid>
                <Button className={classes.sendEmailBtn} onClick={sendAllocationEmail}>{translation.send_email}</Button>
              </Grid>
            ) : (
              <ReportTable key={Math.random()} rows={reportData} groupByOption={groupBy} translation={translation} columns={columns} selectedColumns={selectedColumns} onSelectColumns={setSelectedColumns} />
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
